import React, { useEffect } from "react";
import { useAIAssessmentContext } from "../../../../Context/AIAssessmentContext/AIAssessmentContext";
import useAiAssessmentViolationCapture from "../../hooks/useAiAssessmentViolationCapture";
import useAiAssessmentEvaluate from "../../hooks/useAiAssessmentEvaluate";

const suspiciousCombos = [
  // Browser tab/window controls
  "meta+r",               // Mac refresh
  "meta+tab",             // Mac app switch
  "meta+q",               // Mac quit app
  "meta+w",               // Mac close tab
  "control+r",            // Refresh
  "control+w",            // Close tab
  "control+tab",          // Next tab
  "control+f4",           // Close tab
  "control+n",            // New window
  "meta+n",               // Mac new window
  
  // Window switching (also caught by visibilitychange)
  "alt+tab",              // Windows window switch
  "alt+escape",           // Windows cycle windows
  "alt+f4",               // Close window
  
  // Task Manager & System
  "control+shift+escape", // Task Manager (Windows)
  "control+shift+delete", // Clear browser data
  
  // DevTools - Chrome/Edge
  "control+shift+i",      // DevTools
  "control+shift+c",      // Inspect element
  "control+shift+j",      // Console
  "control+shift+p",      // Command menu
  
  // DevTools - Firefox
  "control+shift+k",      // Firefox console
  "control+shift+e",      // Firefox network tab
  "control+shift+m",      // Firefox responsive mode
  
  // DevTools - Mac
  "meta+shift+i",         // Mac DevTools
  "meta+shift+c",         // Mac inspect element
  "meta+shift+j",         // Mac console
  
  // View source
  "control+u",            // View source
  "meta+u",               // Mac view source
  "control+shift+u",      // View source (Firefox)
  
  // File operations
  "control+s",            // Save page
  "control+o",            // Open file
  "control+p",            // Print dialog
  "meta+s",               // Mac save
  "meta+o",               // Mac open
  "meta+p",               // Mac print
  
  // Screenshot shortcuts
  "meta+shift+3",         // Mac full screenshot
  "meta+shift+4",         // Mac selection screenshot
  "meta+shift+5",         // Mac screenshot options
  "control+shift+s",      // Windows snipping tool (some configs)
  
  // Search & Address bar
  "control+l",            // Focus address bar
  "control+k",            // Focus search bar
  "meta+l",               // Mac address bar
  "alt+d",                // Address bar (Windows)
  
  // History & Bookmarks
  "control+h",            // History
  "control+j",            // Downloads
  "control+d",            // Bookmark
  "meta+h",               // Mac history
  "meta+j",               // Mac downloads
  "meta+d",               // Mac bookmark
];

const plainFunctionKeysToCatch = [
  "F1",           // Help (could open external window)
  "F3",           // Find (some browsers)
  "F4",           // Address bar (some browsers)
  "F5",           // Refresh
  "F6",           // Focus address bar
  "F7",           // Caret browsing
  "F8",           // Various
  "F9",           // Various
  "F10",          // Menu bar
  "F11",          // Fullscreen toggle
  "F12",          // DevTools
  "PrintScreen",  // Screenshot
];

const AiAssessmentProctoring: React.FC = () => {
  // States form ai-assessment context
  const {
    setIsFullscreen,
    isEvaluating,
    setShowRightClickDisabledModal,
    showCursorFocusChangeModal,
    showTabSwitchModal,
    showRightClickDisabledModal,
    showSuspiciousKeyPressModal,
    setShowTabSwitchModal,
    setShowCursorFocusChangeModal,
    setShowSuspiciousKeyPressModal,
    setPressedSuspiciousKey,
    setShowExitFullScreenModal,
    exitFullScreenTimer,
    setExitFullScreenTimer,
    cursorFocuChangeModalRef,
    fullscreenElementRef,
    isRoundFinished,
    currentStep,
    showExitFullScreenModal,
    displayStreamRef,
    currentSession,
    setIsOffline,
  } = useAIAssessmentContext();

  const isAnyModalOpen =
    showCursorFocusChangeModal ||
    showExitFullScreenModal ||
    showRightClickDisabledModal ||
    showSuspiciousKeyPressModal ||
    showTabSwitchModal;

  const isTimerRunning = exitFullScreenTimer;

  const isRoundActive =
    !isRoundFinished &&
    currentStep >= 0 &&
    !isEvaluating &&
    currentSession &&
    currentSession.status === "pending";

  const isNoViolationShowing = !isAnyModalOpen && !isTimerRunning;

  const shouldTrigger = isRoundActive;

  const {
    captureNotAllowedKeyPressing,
    captureMultipleTabOpening,
    captureCursorFocusChange,
    captureScreenRecordingStopped,
    captureFullScreenExitAttempt,
  } = useAiAssessmentViolationCapture();

  const { stopAndUploadRecording } = useAiAssessmentEvaluate();

  // Effect to detect fullscreen exit
  useEffect(() => {
    const handleFullscreenChange = async () => {
      if (!shouldTrigger) return;
      const fsElement =
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement;
      const isNowFullscreen = fsElement === fullscreenElementRef.current;

      setIsFullscreen(isNowFullscreen);
      if (!isNowFullscreen) {
        setShowExitFullScreenModal(true);
        setExitFullScreenTimer(true);
        await captureFullScreenExitAttempt();
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrigger]);

  // Effect to capture tab switch
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!shouldTrigger) return;
      // Don't trigger if another violation modal is already showing
      if (isAnyModalOpen) return;

      if (document.hidden) {
        setShowTabSwitchModal(true);
        captureMultipleTabOpening();
      } else {
        console.log("User returned to the tab.");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrigger, isAnyModalOpen]);

  // Effect to periodically verify fullscreen state
useEffect(() => {
  if (!shouldTrigger) return;
  
  const verifyFullscreenState = () => {
    const currentFullscreen = !!(
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement
    );
    
    const expectedFullscreen = fullscreenElementRef.current !== null;
    
    // Detect if we should be fullscreen but aren't
    if (expectedFullscreen && !currentFullscreen && !showExitFullScreenModal && !isAnyModalOpen) {
      console.warn("[Proctoring] Fullscreen state mismatch detected");
      setIsFullscreen(false);
      setShowExitFullScreenModal(true);
      setExitFullScreenTimer(true);
      captureFullScreenExitAttempt();
    }
  };
  
  const intervalId = setInterval(verifyFullscreenState, 1000);
  
  return () => clearInterval(intervalId);
}, [shouldTrigger, showExitFullScreenModal, isAnyModalOpen]);

  // Effect to disable right-click during AI Assessment
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      if (!shouldTrigger) return;
      e.preventDefault();
      e.stopPropagation();
      setShowRightClickDisabledModal(true);
    };

    document.addEventListener("contextmenu", handleContextMenu);
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrigger]);

  // Effect to capture cursor focus change
  useEffect(() => {
    let browserChromeExitTimeoutId: ReturnType<typeof setTimeout> | null = null;
    
    const handleMouseLeave = (e: MouseEvent) => {
      if (!shouldTrigger || !isNoViolationShowing) return;

      const relatedTarget = e.relatedTarget as Node | null;
      
      const isInModal =
        cursorFocuChangeModalRef?.current &&
        relatedTarget &&
        cursorFocuChangeModalRef.current.contains(relatedTarget);

      if (isInModal) return;

      // Check if cursor left to browser chrome (screen share bar, browser UI, etc.)
      // relatedTarget is null when leaving to browser-rendered elements (any position)
      // This handles screen share bar regardless of where user moved it
      const isBrowserChromeExit = relatedTarget === null;

      if (isBrowserChromeExit) {
        // Clear any existing timeout first
        if (browserChromeExitTimeoutId) {
          clearTimeout(browserChromeExitTimeoutId);
        }
        
        console.log("[Proctoring] Browser chrome exit detected - starting grace period");
        
        // Allow brief interaction with browser UI (2.5 seconds grace period)
        // This handles screen share bar at ANY position on screen
        browserChromeExitTimeoutId = setTimeout(() => {
          // Check if document still has focus after grace period
          // If focus is lost, something suspicious happened (opened another app)
          if (shouldTrigger && !document.hasFocus()) {
            console.log("[Proctoring] Document lost focus after browser chrome exit - triggering violation");
            setShowCursorFocusChangeModal(true);
            captureCursorFocusChange();
          }
          browserChromeExitTimeoutId = null;
        }, 500);
        
        return;
      }

      // Exit to another DOM element (not browser chrome): trigger after brief delay
      // This shouldn't normally happen in fullscreen, but handle it anyway
      setTimeout(() => {
        if (shouldTrigger) {
          setShowCursorFocusChangeModal(true);
          captureCursorFocusChange();
        }
      }, 50);
    };

    const handleMouseEnter = () => {
      // Cursor returned - cancel any pending timeout
      if (browserChromeExitTimeoutId) {
        clearTimeout(browserChromeExitTimeoutId);
        browserChromeExitTimeoutId = null;
        console.log("[Proctoring] Cursor returned - grace period cancelled");
      }
    };

    const handleVisibilityChangeDuringGracePeriod = () => {
      // If document becomes hidden while in grace period, trigger immediately
      // This catches Alt+Tab, opening another app, etc.
      if (document.hidden && browserChromeExitTimeoutId && shouldTrigger) {
        clearTimeout(browserChromeExitTimeoutId);
        browserChromeExitTimeoutId = null;
        console.log("[Proctoring] Document hidden during grace period - triggering violation");
        setShowCursorFocusChangeModal(true);
        captureCursorFocusChange();
      }
    };

    const container = fullscreenElementRef?.current;
    if (container) {
      container.addEventListener("mouseleave", handleMouseLeave);
      container.addEventListener("mouseenter", handleMouseEnter);
      document.addEventListener("visibilitychange", handleVisibilityChangeDuringGracePeriod);
    }

    return () => {
      if (browserChromeExitTimeoutId) clearTimeout(browserChromeExitTimeoutId);
      if (container) {
        container.removeEventListener("mouseleave", handleMouseLeave);
        container.removeEventListener("mouseenter", handleMouseEnter);
        document.removeEventListener("visibilitychange", handleVisibilityChangeDuringGracePeriod);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldTrigger,
    cursorFocuChangeModalRef,
    fullscreenElementRef,
    isNoViolationShowing,
  ]);

  // Effect to capture suspicious key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!shouldTrigger) return;
      const rawKey = e.key;
      const key = rawKey.toLowerCase();

      if (key === "escape") {
        e.preventDefault();
        e.stopPropagation();
        setShowSuspiciousKeyPressModal(true);
        setPressedSuspiciousKey("Escape");
        captureNotAllowedKeyPressing(["Escape"]);
        return;
      }

      if (
        plainFunctionKeysToCatch.includes(rawKey.toUpperCase()) &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.metaKey &&
        !e.shiftKey &&
        shouldTrigger
      ) {
        e.preventDefault();
        e.stopPropagation();
        setShowSuspiciousKeyPressModal(true);
        setPressedSuspiciousKey(rawKey);
        captureNotAllowedKeyPressing([rawKey]);
        return;
      }

      if (["control", "meta", "alt", "shift"].includes(key)) return;

      const modifiers = [];
      if (e.ctrlKey) modifiers.push("control");
      if (e.metaKey) modifiers.push("meta");
      if (e.altKey) modifiers.push("alt");
      if (e.shiftKey) modifiers.push("shift");

      if (modifiers.length === 0) return;

      const keyCombo = [...modifiers, key].join("+");

      if (suspiciousCombos.includes(keyCombo) && shouldTrigger) {
        e.preventDefault();
        e.stopPropagation();
        setShowSuspiciousKeyPressModal(true);
        setPressedSuspiciousKey(keyCombo);
        captureNotAllowedKeyPressing([keyCombo]);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [shouldTrigger]);

  // Effect to capture screen recording stopped
  useEffect(() => {
    if (!shouldTrigger) {
      return;
    }

    let attachedTracks: MediaStreamTrack[] = [];
    let intervalId: number | undefined;
    let handled = false;

    const onEnded = () => {
      if (handled) return;
      handled = true;
      if (currentSession && shouldTrigger) {
        try {
          captureScreenRecordingStopped();
        } catch {}
      }
    };

    const attachIfNeeded = () => {
      const stream = displayStreamRef?.current;
      if (!stream) return;
      const videoTracks = stream.getVideoTracks();
      videoTracks.forEach((track) => {
        if (!attachedTracks.includes(track)) {
          try {
            track.addEventListener("ended", onEnded);
            attachedTracks.push(track);
          } catch {}
        }
      });
    };

    attachIfNeeded();
    intervalId = window.setInterval(attachIfNeeded, 500);

    return () => {
      try {
        attachedTracks.forEach((track) => {
          try {
            track.removeEventListener("ended", onEnded);
          } catch {}
        });
        attachedTracks = [];
      } catch {}
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [shouldTrigger, currentSession, displayStreamRef]);

  // Effect to capture network status
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    const checkInternet = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000);

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/ping`,
          {
            method: "GET",
            signal: controller.signal,
            cache: "no-store",
          }
        );

        clearTimeout(timeoutId);

        if (response.ok || response.status === 401) {
          setIsOffline(false);
        } else {
          setIsOffline(true);
        }
      } catch {
        setIsOffline(true);
      } finally {
        if (intervalId) clearInterval(intervalId);

        const isPending = currentSession && currentSession.status === "pending";

        intervalId = setInterval(checkInternet, isPending ? 1500 : 15000);
      }
    };

    checkInternet();

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [currentSession]);

  // Effect to capture page refresh during evaluation
  useEffect(() => {
    if (!isEvaluating) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
      localStorage.setItem("refresh", "true");
      localStorage.setItem("refreshedSessionId", currentSession?._id || "");
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isEvaluating, currentSession]);

  // Effect to capture page refresh during AI Assessment
  useEffect(() => {
    const handleBeforeUnload = async (event: BeforeUnloadEvent) => {
      if (!shouldTrigger) return;
      try {
        console.log("AI Assessment Proctoring Page refresh detected");
        const urlParams = new URLSearchParams(window.location.search);
        const sessionIdFromUrl = urlParams.get("sessionId");
        await stopAndUploadRecording(sessionIdFromUrl || undefined);
      } catch (error) {
        console.error("API call failed during refresh:", error);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [shouldTrigger]);

  return null;
};

export default AiAssessmentProctoring;
